"use client";

import React from 'react';
import Link from 'next/link';
import { BlogSoundEmbed } from '@/components/ui/blog-sound-embed';
import type { Sound } from '@/lib/supabase';
import { PUBLIC_SOUND_COUNT_LABEL } from '@/lib/public-sound-count';
import { primaryCtaClass } from '@/lib/guide-ui';

interface RankedSection {
  label: string;
  href: string;
  sounds: Sound[];
}

interface BestSoundsRankedListProps {
  /** Top 25 sounds sorted by downloads (full ranked list) */
  topSounds: Sound[];
  /** Category mini-sections */
  sections: RankedSection[];
}

export function BestSoundsRankedList({ topSounds, sections }: BestSoundsRankedListProps) {
  return (
    <div className="space-y-12">
      {/* Full ranked list */}
      <section>
        <div className="space-y-2">
          {topSounds.map((sound, index) => (
            <div key={sound.id} className="flex items-center gap-3">
              {/* Rank numerals are ink, not accent: the accent's six roles
                  (signal, focus, one primary control, selection,
                  confirmation, links) do not include "top three". */}
              <span
                className="flex-shrink-0 w-8 text-right text-sm font-semibold tabular-nums"
                style={{ color: index < 3 ? 'var(--tls-fg-strong)' : 'var(--tls-fg-subtle)' }}
              >
                #{index + 1}
              </span>
              <div className="flex-1 min-w-0">
                <BlogSoundEmbed sound={sound} />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 text-center">
          <Link
            href="/sounds"
            className={`${primaryCtaClass} px-5 py-2.5 text-sm`}
          >
            Browse All {PUBLIC_SOUND_COUNT_LABEL} Sounds →
          </Link>
        </div>
      </section>

      {/* Mini-sections by category */}
      {sections.map((section) => (
        <section key={section.label}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-[var(--tls-fg-strong)]">{section.label}</h3>
            <Link
              href={section.href}
              className="text-sm text-[color:var(--tls-brand-ink)] hover:text-[color:var(--tls-brand-ink-hover)] transition-colors"
            >
              See all →
            </Link>
          </div>
          {section.sounds.length > 0 ? (
            <div className="space-y-2">
              {section.sounds.map((sound) => (
                <BlogSoundEmbed key={sound.id} sound={sound} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--tls-fg-subtle)]">
              No sounds in this category yet.{' '}
              <Link href={section.href} className="text-[color:var(--tls-brand-ink)] hover:underline">browse all →</Link>
            </p>
          )}
        </section>
      ))}
    </div>
  );
}
